import clsx from "clsx";
import { Users, X } from "lucide-react";
import { useEffect, useRef } from "react";

import { useSession } from "@/session/SessionProvider";

import { useChat } from "./ChatProvider";
import { ChatSurface } from "./ChatSurface";
import type { ChatTab } from "./types";

const TABS: ReadonlyArray<{ id: ChatTab; label: string }> = [
  { id: "chat", label: "Chat" },
  { id: "friends", label: "Friends" },
  { id: "vip", label: "VIP" },
  { id: "support", label: "Support" },
];
const FOCUSABLE =
  'button:not(:disabled):not([tabindex="-1"]), [href], textarea:not(:disabled), input:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function ChatDrawer() {
  const chat = useChat();
  const { session } = useSession();
  const { close, isOpen, replyTarget, setReplyTarget } = chat;
  const drawerRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    document.body.classList.toggle("chat-panel-open", isOpen);
    if (!isOpen) return;
    const mobile = window.matchMedia("(max-width: 479px)").matches;
    const previousOverflow = document.body.style.overflow;
    if (mobile) document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() =>
      closeRef.current?.focus(),
    );
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (replyTarget) {
          setReplyTarget(null);
          return;
        }
        close();
        return;
      }
      if (event.key !== "Tab" || !drawerRef.current) return;
      const focusable = [
        ...drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      if (mobile) document.body.style.overflow = previousOverflow;
      document.body.classList.remove("chat-panel-open");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [close, isOpen, replyTarget, setReplyTarget]);

  return (
    <>
      <button
        aria-label="Close Social"
        className={clsx("chat-backdrop", chat.isOpen && "is-open")}
        onClick={chat.close}
        tabIndex={chat.isOpen ? 0 : -1}
        type="button"
      />
      <aside
        aria-hidden={!chat.isOpen}
        aria-label="Social"
        aria-modal={chat.isOpen || undefined}
        className={clsx("chat-drawer", chat.isOpen && "is-open")}
        ref={drawerRef}
        role="dialog"
      >
        <header className="chat-drawer__header">
          <span className="chat-drawer__art" aria-hidden>
            <img
              alt=""
              src="/app/static/themes/monkeytilt/chat-bubbles-3d.png"
            />
          </span>
          <h3>Social</h3>
          <button
            aria-label="Close"
            onClick={chat.close}
            ref={closeRef}
            type="button"
          >
            <X aria-hidden />
          </button>
        </header>
        <nav aria-label="Social sections" className="chat-tabs" role="tablist">
          <span
            aria-hidden
            className="chat-tabs__indicator"
            style={{
              transform: `translateX(calc(${TABS.findIndex(({ id }) => id === chat.activeTab)} * (100% + 2px)))`,
            }}
          />
          {TABS.map(({ id, label }) => (
            <button
              aria-controls={`chat-panel-${id}`}
              aria-selected={chat.activeTab === id}
              className={chat.activeTab === id ? "is-active" : ""}
              id={`chat-tab-${id}`}
              key={id}
              onClick={() => chat.selectTab(id)}
              role="tab"
              type="button"
            >
              {label}
              {id === "chat" &&
              chat.activeTab !== "chat" &&
              chat.snapshot.unreadCount > 0 ? (
                <span
                  className={chat.snapshot.unreadMentions ? "is-mention" : ""}
                >
                  {chat.snapshot.unreadMentions
                    ? "@"
                    : chat.snapshot.unreadCount > 99
                      ? "99+"
                      : chat.snapshot.unreadCount}
                </span>
              ) : null}
            </button>
          ))}
        </nav>

        {TABS.map(({ id }) => (
          <section
            aria-labelledby={`chat-tab-${id}`}
            className="chat-drawer__panel"
            hidden={chat.activeTab !== id}
            id={`chat-panel-${id}`}
            key={id}
            role="tabpanel"
          >
            {id === "chat" ? (
              <ChatSurface />
            ) : (
              <AuxiliaryPanel authenticated={Boolean(session)} tab={id} />
            )}
          </section>
        ))}
      </aside>
    </>
  );
}

function AuxiliaryPanel({
  authenticated,
  tab,
}: {
  authenticated: boolean;
  tab: Exclude<ChatTab, "chat">;
}) {
  if (tab === "friends") {
    return (
      <div className="chat-placeholder chat-placeholder--friends">
        <span>
          <Users aria-hidden />
        </span>
        <p>Coming soon</p>
      </div>
    );
  }
  const label = tab === "vip" ? "VIP chat" : "support";
  return (
    <p className="chat-placeholder chat-placeholder--plain">
      {authenticated
        ? `${label === "VIP chat" ? "VIP messaging" : "Support"} is not connected for this tenant.`
        : `Sign in to access ${label}`}
    </p>
  );
}
